import { useQuery } from '@tanstack/react-query';
import { graphql } from '@/generated/graphql';
import type {
  GetCountriesByDepartmentIdQueryVariables,
  GetCurrentUserDepartmentsQueryVariables,
  GetUserDepartmentsQueryVariables,
} from '@/generated/graphql/graphql';
import { getGraphQLClient } from '../graphqlClient';
import { createMutationHook } from '../hooks/typedHookFactory';
import { QueryKeys } from '../queryKeys';

const GET_COUNTRIES_BY_DEPARTMENT_ID = graphql(`
  query GetCountriesByDepartmentId($departmentId: Int!) {
    country(where: { departments: { id: { _eq: $departmentId } } }) {
      id
      name
    }
  }
`);

const GET_USER_DEPARTMENTS = graphql(`
  query GetUserDepartments($ids: [Int!]) {
    department(where: { id: { _in: $ids } }) {
      id
      name
      countryId
      currency
      language
      timeZone
      is_express_enabled
      disabled_express_reason
    }
  }
`);

const GET_CURRENT_USER_DEPARTMENTS = graphql(`
  query GetCurrentUserDepartments($userId: String!) {
    departments_users(where: { userId: { _eq: $userId } }) {
      departmentId
      department {
        id
        name
        countryId
        currency
        language
        timeZone
        is_express_enabled
        disabled_express_reason
      }
    }
  }
`);

export const useGetCountriesByDepartmentId = (
  variables: GetCountriesByDepartmentIdQueryVariables
) => {
  const client = getGraphQLClient();
  return useQuery({
    queryKey: [QueryKeys.GET_COUNTRIES_BY_DEPARTMENT_ID, variables.departmentId],
    queryFn: () => client.request(GET_COUNTRIES_BY_DEPARTMENT_ID, variables),
  });
};

export const useGetUserDepartments = (variables: GetUserDepartmentsQueryVariables) => {
  const client = getGraphQLClient();

  return useQuery({
    queryKey: [QueryKeys.GET_USER_DEPARTMENTS, variables.ids],
    queryFn: () => client.request(GET_USER_DEPARTMENTS, variables),
    enabled: Boolean(variables.ids),
  });
};

export const useGetCurrentUserDepartments = (
  variables: GetCurrentUserDepartmentsQueryVariables
) => {
  const client = getGraphQLClient();

  return useQuery({
    queryKey: [QueryKeys.GET_CURRENT_USER_DEPARTMENTS, variables.userId],
    queryFn: () => client.request(GET_CURRENT_USER_DEPARTMENTS, variables),
    enabled: Boolean(variables.userId),
  });
};

const TOGGLE_DEPARTMENT_EXPRESS = graphql(`
  mutation ToggleDepartmentExpress($data: ToggleInput!) {
    toggleDepartmentExpress(data: $data) {
      __typename
    }
  }
`);

export const useToggleDepartmentExpress = createMutationHook(TOGGLE_DEPARTMENT_EXPRESS, {
  invalidateQueries: [
    {
      queryKey: () => [QueryKeys.GET_USER_DEPARTMENTS],
    },
    {
      queryKey: () => [QueryKeys.GET_CURRENT_USER_DEPARTMENTS],
    },
  ],
  errorMessage: 'Failed to toggle department express',
});
